import React, { createContext, useState, useEffect, useContext } from 'react';

const CartContext = createContext();

const calculateDiscountedPrice = (price, type, value) => {
    const p = parseFloat(price);
    const v = parseFloat(value);
    if (!v || v <= 0) return p;
    if (type === 'percentage') {
        return p - (p * (v / 100));
    } else {
        return p - v;
    }
};

const getEffectiveDiscount = (product, variation) => {
    if (variation && variation.discount_is_active === 1 && parseFloat(variation.discount_value) > 0) {
        return { type: variation.discount_type, value: variation.discount_value };
    }
    if (product && product.discount_is_active === 1 && parseFloat(product.discount_value) > 0) {
        return { type: product.discount_type, value: product.discount_value };
    }
    return null;
};

const parseOffer = (product) => {
    if (!product) return null;

    let offerQty = product.offer_qty !== undefined && product.offer_qty !== null
        ? parseInt(product.offer_qty, 10)
        : null;
    let offerPrice = product.offer_price !== undefined && product.offer_price !== null
        ? parseFloat(product.offer_price)
        : null;
    let groupId = null;

    if ((!offerQty || offerQty <= 0 || Number.isNaN(offerQty)) && product.offerDetails) {
        const parts = product.offerDetails.toString().split(':');
        offerQty = parseInt(parts[0], 10);
        offerPrice = parseFloat(parts[1]);
        groupId = parts[2] || null;
    }

    if (!offerQty || offerQty <= 0 || Number.isNaN(offerQty) || Number.isNaN(offerPrice)) return null;
    return { offerQty, offerPrice, groupId };
};

const getOfferTotal = (product, quantity) => {
    const offer = parseOffer(product);
    if (!offer) return null;

    const unitPrice = parseFloat(product.Price);
    if (Number.isNaN(unitPrice)) return null;

    const bundles = Math.floor(quantity / offer.offerQty);
    const remainder = quantity % offer.offerQty;
    const bundleTotal = bundles * offer.offerPrice;
    const remainderTotal = remainder * unitPrice;
    const total = bundleTotal + remainderTotal;

    return {
        bundles,
        remainder,
        offerQty: offer.offerQty,
        offerPrice: offer.offerPrice,
        total,
        averageUnitPrice: quantity > 0 ? total / quantity : unitPrice,
    };
};

// Group cart items by product and calculate combined offer
const getProductGroupOfferInfo = (cartItems, productId) => {
    const productItems = cartItems.filter(item => item.product.id === productId);
    if (productItems.length === 0) return null;

    // Get the product (they all have the same product object)
    const product = productItems[0].product;
    const offer = parseOffer(product);
    if (!offer) return null;

    // Calculate total quantity across all variations of this product
    const totalQuantity = productItems.reduce((sum, item) => sum + item.quantity, 0);
    const unitPrice = parseFloat(product.Price);
    if (Number.isNaN(unitPrice)) return null;

    // Calculate offer for combined quantity
    const bundles = Math.floor(totalQuantity / offer.offerQty);
    const remainder = totalQuantity % offer.offerQty;
    const bundleTotal = bundles * offer.offerPrice;
    const remainderTotal = remainder * unitPrice;
    const totalCost = bundleTotal + remainderTotal;

    // Calculate savings
    const regularTotal = totalQuantity * unitPrice;
    const savings = regularTotal - totalCost;
    const savingsPerUnit = totalQuantity > 0 ? savings / totalQuantity : 0;

    return {
        bundles,
        remainder,
        offerQty: offer.offerQty,
        offerPrice: offer.offerPrice,
        totalQuantity,
        totalCost,
        regularTotal,
        savings,
        savingsPerUnit,
        isApplicable: totalQuantity >= offer.offerQty,
    };
};

// Group cart items by groupId and calculate cross-product offer
const getMixAndMatchOfferInfo = (cartItems, groupId) => {
    const groupItems = cartItems.filter(item => {
        const offer = parseOffer(item.product);
        return offer && offer.groupId === groupId;
    });

    if (groupItems.length === 0) return null;

    // Use offer details from the first item
    const offer = parseOffer(groupItems[0].product);

    // Collect all individual units to sort them
    let allUnits = [];
    groupItems.forEach(item => {
        const discount = getEffectiveDiscount(item.product, item.variation);
        const price = discount
            ? calculateDiscountedPrice(item.product.Price, discount.type, discount.value)
            : parseFloat(item.product.Price);
        for (let i = 0; i < item.quantity; i++) {
            allUnits.push(price);
        }
    });

    // Sort descending so most expensive items are bundled, maximizing savings
    allUnits.sort((a, b) => b - a);

    const totalQuantity = allUnits.length;
    const bundles = Math.floor(totalQuantity / offer.offerQty);
    const remainder = totalQuantity % offer.offerQty;

    const bundleTotal = bundles * offer.offerPrice;
    
    // Remainder items are the cheapest ones at the end of the array
    let remainderTotal = 0;
    for (let i = totalQuantity - remainder; i < totalQuantity; i++) {
        remainderTotal += allUnits[i];
    }

    const totalCost = bundleTotal + remainderTotal;
    const regularTotal = allUnits.reduce((sum, price) => sum + price, 0);
    const savings = regularTotal - totalCost;
    const savingsPerUnit = totalQuantity > 0 ? savings / totalQuantity : 0;

    return {
        bundles,
        remainder,
        offerQty: offer.offerQty,
        offerPrice: offer.offerPrice,
        totalQuantity,
        totalCost,
        regularTotal,
        savings,
        savingsPerUnit,
        isApplicable: totalQuantity >= offer.offerQty,
        groupItems
    };
};

export const CartProvider = ({ children }) => {
    const [cart, setCart] = useState([]);
    const [isCartOpen, setIsCartOpen] = useState(false); // Optional, if we want a slide-out cart later

    useEffect(() => {
        // Load cart from local storage on mount
        const storedCart = localStorage.getItem('cart');
        if (storedCart) {
            setCart(JSON.parse(storedCart));
        }
    }, []);

    useEffect(() => {
        // Save cart to local storage whenever it changes
        localStorage.setItem('cart', JSON.stringify(cart));
    }, [cart]);

    const addToCart = (product, variation, quantity = 1) => {
        setCart(prevCart => {
            const existingItemIndex = prevCart.findIndex(
                item => item.product.id === product.id &&
                    item.variation?.varID === variation?.varID
            );

            const stock = variation ? variation.Qty : product.StockQTY;
            const stockNum = parseInt(stock || 0);
            const maxQty = stockNum;

            if (existingItemIndex >= 0) {
                // Item exists, increase quantity
                const newCart = [...prevCart];
                let newQty = newCart[existingItemIndex].quantity + quantity;
                if (newQty > maxQty) {
                    newQty = maxQty;
                }
                newCart[existingItemIndex].quantity = newQty;
                return newCart;
            } else {
                // New item
                let newQty = quantity;
                if (newQty > maxQty) {
                    newQty = maxQty;
                }
                if (newQty > 0) {
                    return [...prevCart, { product, variation, quantity: newQty }];
                }
                return prevCart;
            }
        });
    };

    const removeFromCart = (productId, variationId) => {
        setCart(prevCart => prevCart.filter(
            item => !(item.product.id === productId && item.variation?.varID === variationId)
        ));
    };

    const updateQuantity = (productId, variationId, newQuantity) => {
        if (newQuantity < 1) return;
        setCart(prevCart => {
            const newCart = [...prevCart];
            const itemIndex = newCart.findIndex(
                item => item.product.id === productId && item.variation?.varID === variationId
            );
            if (itemIndex >= 0) {
                const item = newCart[itemIndex];
                const stock = item.variation ? item.variation.Qty : item.product.StockQTY;
                const stockNum = parseInt(stock || 0);
                const maxQty = stockNum;
                let safeQty = newQuantity;
                if (safeQty > maxQty) {
                    safeQty = maxQty;
                }
                if (safeQty >= 1) {
                    newCart[itemIndex].quantity = safeQty;
                }
            }
            return newCart;
        });
    };

    const clearCart = () => {
        setCart([]);
    };

    const cartTotal = (() => {
        let total = 0;

        // Find all unique groupIds in the cart
        const groupIds = new Set();
        const nonGroupedCart = [];

        cart.forEach(item => {
            const offer = parseOffer(item.product);
            if (offer && offer.groupId) {
                groupIds.add(offer.groupId);
            } else {
                nonGroupedCart.push(item);
            }
        });

        // Calculate mix and match totals
        for (const groupId of groupIds) {
            const mixMatchOffer = getMixAndMatchOfferInfo(cart, groupId);
            if (mixMatchOffer && mixMatchOffer.isApplicable) {
                total += mixMatchOffer.totalCost;
            } else if (mixMatchOffer) {
                // Not enough items for bundle, just add regular price
                total += mixMatchOffer.regularTotal;
            }
        }

        // Calculate non-grouped products (same product groupings)
        const productIds = [...new Set(nonGroupedCart.map(item => item.product.id))];

        for (const productId of productIds) {
            const groupOffer = getProductGroupOfferInfo(nonGroupedCart, productId);

            if (groupOffer && groupOffer.isApplicable) {
                // Use combined offer price
                total += groupOffer.totalCost;
            } else {
                // No applicable group offer, calculate per-item
                const productItems = nonGroupedCart.filter(item => item.product.id === productId);
                for (const item of productItems) {
                    const itemOffer = getOfferTotal(item.product, item.quantity);
                    if (itemOffer) {
                        total += itemOffer.total;
                    } else {
                        const discount = getEffectiveDiscount(item.product, item.variation);
                        const price = discount
                            ? calculateDiscountedPrice(item.product.Price, discount.type, discount.value)
                            : parseFloat(item.product.Price);
                        total += price * item.quantity;
                    }
                }
            }
        }

        return total;
    })();

    const cartCount = cart.reduce((count, item) => count + item.quantity, 0);

    return (
        <CartContext.Provider value={{
            cart,
            addToCart,
            removeFromCart,
            updateQuantity,
            clearCart,
            cartTotal,
            cartCount,
            isCartOpen,
            setIsCartOpen,
            getProductGroupOfferInfo,
            getMixAndMatchOfferInfo,
            parseOffer
        }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);
